import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CircleMarker,
  MapContainer,
  Marker,
  Polyline,
  Popup,
  TileLayer,
  useMap,
} from "react-leaflet";
import { DivIcon } from "leaflet";
import apiClient, { initializeApiClient } from "../../api/apiClient";

const STATUS_UI = {
  moving: {
    tag: "Sending",
    hint: "GPS is coming in.",
    tone: "ok",
  },
  stationary: {
    tag: "Stationary",
    hint: "App is sending. Vehicle is not moving.",
    tone: "ok",
  },
  not_sending: {
    tag: "Not sending",
    hint: "No GPS for 5 minutes during hours.",
    tone: "problem",
  },
  outside_hours: {
    tag: "Expected",
    hint: "Outside working hours.",
    tone: "ok",
  },
};

const TONE = {
  ok: { bg: "#e7f6ea", border: "#b7e0c0", tag: "#1b9e3e" },
  problem: { bg: "#fde8e8", border: "#f0b4b4", tag: "#c62828" },
};

const ROUTE_COLORS = ["#0b72c6", "#c47b00", "#7b2cbf", "#1b9e3e", "#d62828"];
const POINT_MARKER_CAP = 400;

function FitPins({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    map.fitBounds(points, { padding: [36, 36], maxZoom: 13 });
  }, [map, points]);
  return null;
}

function pinIcon(driver) {
  const tone = STATUS_UI[driver.status]?.tone || (driver.expected ? "ok" : "problem");
  const color = TONE[tone]?.tag || "#1b9e3e";
  return new DivIcon({
    className: "owner-live-pin",
    html: `<div style="background:${color};color:#fff;border-radius:14px;padding:4px 8px;font-size:12px;box-shadow:0 1px 4px rgba(0,0,0,.3);white-space:nowrap;border:none;">${driver.name}</div>`,
    iconSize: [90, 28],
    iconAnchor: [45, 28],
  });
}

function routeColor(index) {
  return ROUTE_COLORS[index % ROUTE_COLORS.length];
}

function Tag({ children, color }) {
  return (
    <span
      className="badge me-1 mb-1"
      style={{ background: color, fontWeight: 600 }}
    >
      {children}
    </span>
  );
}

function hoursLabel(driver) {
  if (driver.start_working_hour && driver.end_working_hour) {
    return `${driver.start_working_hour}–${driver.end_working_hour}`;
  }
  return "Hours not set";
}

function datesLabel(driver) {
  if (driver.start_date || driver.end_date) {
    return `${driver.start_date || "?"} → ${driver.end_date || "?"}`;
  }
  return "Dates not set";
}

function DriverCard({ driver }) {
  const ui = STATUS_UI[driver.status] || STATUS_UI.not_sending;
  const tone = driver.expected === false ? TONE.problem : TONE[ui.tone];
  return (
    <div
      className="rounded-3 p-2 mb-2"
      style={{ background: tone.bg, border: `1px solid ${tone.border}` }}
    >
      <div className="d-flex justify-content-between gap-2">
        <strong>{driver.name}</strong>
        <Tag color={tone.tag}>{ui.tag}</Tag>
      </div>
      <div className="mt-1">
        <Tag color={driver.tracking_on ? "#1b9e3e" : "#6b6b6b"}>
          {driver.tracking_on ? "Tracking on" : "Tracking off"}
        </Tag>
        <Tag color="#015A96">{hoursLabel(driver)}</Tag>
        <Tag color="#3E4755">{datesLabel(driver)}</Tag>
        {driver.timezone ? <Tag color="#5c6b7a">{driver.timezone}</Tag> : null}
      </div>
      <div className="small mt-1">{ui.hint}</div>
    </div>
  );
}

function sampledPoints(points) {
  if (points.length <= POINT_MARKER_CAP) return points;
  const step = Math.ceil(points.length / POINT_MARKER_CAP);
  return points.filter((_, index) => index % step === 0);
}

export default function VehicleOwnerLiveMonitor() {
  const navigate = useNavigate();
  const [snapshot, setSnapshot] = useState(null);
  const [trail, setTrail] = useState(null);
  const [error, setError] = useState("");
  const [backendDown, setBackendDown] = useState(false);
  const [campaignFilter, setCampaignFilter] = useState("");
  const [campaignQuery, setCampaignQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [driverFilter, setDriverFilter] = useState("");
  const [liveOnly, setLiveOnly] = useState(false);
  const [loading, setLoading] = useState(true);

  const isAdmin = snapshot?.viewer_role === "admin";

  const load = useCallback(async () => {
    initializeApiClient();
    try {
      const response = await apiClient.get("/campaign/owner-live-monitor/");
      setSnapshot(response.data);
      setBackendDown(false);
      setError("");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        navigate("/signin");
        return;
      }
      if (status === 403) {
        setError("This page is for vehicle owners and admins.");
        setBackendDown(false);
      } else {
        setBackendDown(true);
        setError("Could not reach the tracking server. Backend may be down.");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    if (!campaignFilter) {
      setTrail(null);
      return undefined;
    }
    let cancelled = false;
    const fetchTrail = async () => {
      initializeApiClient();
      try {
        const params = { campaign_id: campaignFilter };
        if (driverFilter) params.driver_id = driverFilter;
        const response = await apiClient.get(
          "/campaign/owner-live-monitor/route/",
          { params }
        );
        if (!cancelled) setTrail(response.data);
      } catch (err) {
        if (!cancelled && err?.response?.status !== 401) {
          setTrail(null);
        }
      }
    };
    fetchTrail();
    const id = setInterval(fetchTrail, 15000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [campaignFilter, driverFilter]);

  const campaignChoices = useMemo(() => {
    const query = campaignQuery.trim().toLowerCase();
    return (snapshot?.campaigns || [])
      .filter((campaign) => !liveOnly || campaign.is_live)
      .filter((campaign) => {
        if (!query) return true;
        const haystack = `${campaign.name} ${campaign.owner_name || ""} ${
          campaign.id
        }`.toLowerCase();
        return haystack.includes(query);
      })
      .slice(0, 25);
  }, [snapshot, campaignQuery, liveOnly]);

  const selectedCampaign = useMemo(
    () =>
      (snapshot?.campaigns || []).find(
        (campaign) => String(campaign.id) === String(campaignFilter)
      ),
    [snapshot, campaignFilter]
  );

  const drivers = useMemo(() => {
    const rows = snapshot?.drivers || [];
    const liveCampaignIds = new Set(
      (snapshot?.campaigns || [])
        .filter((campaign) => campaign.is_live)
        .map((campaign) => String(campaign.id))
    );
    return rows.filter((row) => {
      if (liveOnly && !liveCampaignIds.has(String(row.campaign_id))) {
        return false;
      }
      if (campaignFilter && String(row.campaign_id) !== String(campaignFilter)) {
        return false;
      }
      if (driverFilter && String(row.user_id) !== String(driverFilter)) {
        return false;
      }
      return true;
    });
  }, [snapshot, campaignFilter, driverFilter, liveOnly]);

  const driverChoices = useMemo(() => {
    const rows = snapshot?.drivers || [];
    return rows
      .filter((row) => {
        if (campaignFilter && String(row.campaign_id) !== String(campaignFilter)) {
          return false;
        }
        return true;
      })
      .filter(
        (row, index, all) =>
          all.findIndex((item) => item.user_id === row.user_id) === index
      );
  }, [snapshot, campaignFilter]);

  const mapped = (campaignFilter ? drivers : []).filter(
    (row) => row.latitude != null && row.longitude != null
  );
  const gpsPoints = sampledPoints(trail?.points || []);
  const bounds = [
    ...mapped.map((row) => [row.latitude, row.longitude]),
    ...gpsPoints.map((point) => [point.latitude, point.longitude]),
  ];
  const center = bounds[0] || [37.7577, -122.4376];

  const chooseCampaign = (campaign) => {
    setCampaignFilter(String(campaign.id));
    setCampaignQuery(`${campaign.is_live ? "LIVE — " : ""}${campaign.name}`);
    setSearchOpen(false);
    setDriverFilter("");
  };

  const clearCampaign = () => {
    setCampaignFilter("");
    setCampaignQuery("");
    setSearchOpen(false);
    setTrail(null);
  };

  return (
    <div className="p-3">
      <style>{`.leaflet-div-icon.owner-live-pin{background:transparent;border:none;}`}</style>
      <h2 className="fs-3 mb-2">
        {isAdmin ? "Live campaigns" : "Live drivers"}
      </h2>
      <p className="text-muted mb-3">
        Search a campaign to see its drivers, GPS points, and past route.
        Updates every 5 seconds.
      </p>

      {backendDown && (
        <div className="alert alert-danger" role="alert">
          Backend problem: the monitor could not load a snapshot. Tracking on
          the phones may still be running; this page cannot confirm it until
          the server responds.
        </div>
      )}
      {error && !backendDown && (
        <div className="alert alert-warning" role="alert">
          {error}
        </div>
      )}
      {snapshot && snapshot.ok && !snapshot.heartbeat_available && (
        <div className="alert alert-warning" role="alert">
          Live heartbeat cache is degraded. Status may rely on last saved GPS
          point only.
        </div>
      )}

      <div className="row g-2 mb-3">
        <div className="col-12 col-md-6 position-relative">
          <label className="form-label mb-1">Search campaign</label>
          <input
            className="form-control"
            placeholder="Type campaign name, owner, or id"
            value={campaignQuery}
            onChange={(e) => {
              setCampaignQuery(e.target.value);
              setSearchOpen(true);
              if (!e.target.value) {
                setCampaignFilter("");
                setTrail(null);
              }
            }}
            onFocus={() => setSearchOpen(true)}
          />
          {searchOpen && (
            <div
              className="bg-white border rounded-2 shadow-sm mt-1"
              style={{
                position: "absolute",
                zIndex: 2000,
                width: "100%",
                maxHeight: 240,
                overflow: "auto",
              }}
            >
              {campaignChoices.length === 0 && (
                <div className="p-2 small text-muted">No matching campaigns.</div>
              )}
              {campaignChoices.map((campaign) => (
                <button
                  key={campaign.id}
                  type="button"
                  className="dropdown-item text-start"
                  onClick={() => chooseCampaign(campaign)}
                >
                  {campaign.is_live ? (
                    <span className="badge bg-success me-2">LIVE</span>
                  ) : null}
                  {campaign.name}
                  {campaign.owner_name ? (
                    <span className="text-muted"> · {campaign.owner_name}</span>
                  ) : null}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="col-12 col-md-4">
          <label className="form-label mb-1">Driver</label>
          <select
            className="form-select"
            value={driverFilter}
            onChange={(e) => setDriverFilter(e.target.value)}
            disabled={!campaignFilter}
          >
            <option value="">All assigned drivers</option>
            {driverChoices.map((row) => (
              <option key={row.user_id} value={row.user_id}>
                {row.name}
              </option>
            ))}
          </select>
        </div>
        <div className="col-12 col-md-2 d-flex align-items-end gap-2">
          <div className="form-check mb-2">
            <input
              id="live-only"
              className="form-check-input"
              type="checkbox"
              checked={liveOnly}
              onChange={(e) => setLiveOnly(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="live-only">
              Live only
            </label>
          </div>
        </div>
      </div>

      {selectedCampaign && (
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          <span className="fw-semibold">{selectedCampaign.name}</span>
          {selectedCampaign.is_live ? (
            <Tag color="#1b9e3e">LIVE</Tag>
          ) : (
            <Tag color="#6b6b6b">not live</Tag>
          )}
          <Tag color="#015A96">{hoursLabel(selectedCampaign)}</Tag>
          <Tag color="#3E4755">{datesLabel(selectedCampaign)}</Tag>
          {selectedCampaign.timezone ? (
            <Tag color="#5c6b7a">{selectedCampaign.timezone}</Tag>
          ) : null}
          {selectedCampaign.owner_name ? (
            <span className="small text-muted">{selectedCampaign.owner_name}</span>
          ) : null}
          <span className="small text-muted">
            {(trail?.points || []).length} GPS points
            {trail?.truncated ? " (latest slice)" : ""}
          </span>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={clearCampaign}
          >
            Clear
          </button>
        </div>
      )}

      <div className="row g-3">
        <div className="col-12 col-lg-7">
          <div
            style={{
              height: "55vh",
              minHeight: 280,
              borderRadius: 12,
              overflow: "hidden",
              border: "1px solid #d6d6d6",
            }}
          >
            <MapContainer
              center={center}
              zoom={8}
              style={{ height: "100%", width: "100%" }}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution="&copy; OpenStreetMap contributors"
              />
              {bounds.length > 0 && <FitPins points={bounds} />}
              {(trail?.routes || []).map((route, index) =>
                route.coordinates?.length > 1 ? (
                  <Polyline
                    key={`route-${route.vehicle_id || index}`}
                    positions={route.coordinates}
                    pathOptions={{
                      color: routeColor(index),
                      weight: 4,
                      opacity: 0.85,
                    }}
                  />
                ) : null
              )}
              {gpsPoints.map((point) => (
                <CircleMarker
                  key={`pt-${point.id}`}
                  center={[point.latitude, point.longitude]}
                  radius={4}
                  pathOptions={{
                    color: "#015A96",
                    fillColor: "#015A96",
                    fillOpacity: 0.8,
                    weight: 1,
                  }}
                >
                  <Popup>
                    GPS point
                    <br />
                    {point.vehicle_label}
                    <br />
                    {point.recorded_at}
                  </Popup>
                </CircleMarker>
              ))}
              {mapped.map((driver) => (
                <Marker
                  key={`${driver.campaign_id}-${driver.user_id}`}
                  position={[driver.latitude, driver.longitude]}
                  icon={pinIcon(driver)}
                >
                  <Popup>
                    <strong>{driver.name}</strong>
                    <br />
                    {STATUS_UI[driver.status]?.tag || driver.status}
                    <br />
                    {hoursLabel(driver)}
                    <br />
                    {datesLabel(driver)}
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>
        </div>
        <div className="col-12 col-lg-5">
          <div
            className="bg-white rounded-3 p-3"
            style={{ maxHeight: "55vh", overflow: "auto" }}
          >
            <h3 className="fs-5 mb-3">
              {campaignFilter ? "Drivers" : "Tracking status"}
            </h3>
            {loading && !snapshot && <p>Loading…</p>}
            {!campaignFilter && (
              <p className="text-muted">
                Choose a campaign from search to see drivers, the past route, and
                GPS points.
              </p>
            )}
            {campaignFilter && !loading && drivers.length === 0 && (
              <p className="text-muted">No assigned drivers for this campaign.</p>
            )}
            {campaignFilter &&
              drivers.map((driver) => (
                <DriverCard
                  key={`${driver.campaign_id}-${driver.user_id}`}
                  driver={driver}
                />
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}
